import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Tooltip from '@mui/material/Tooltip'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import api from 'api'
import {
  useDeactivateAccountWithCaptureMutation,
  useRequestAccountDeactivationWithCaptureMutation,
} from 'api/accounts/deactivateAccountWithCapture'
import { CACHE_TAG_CONFIGURED_SERVICES_LIST } from 'api/cacheTagTypes'
import { useGetYardListQuery } from 'api/settings/yard/yardCrud'
import EditAccountDialog from 'components/customer-details/content/account-details/edit/EditAccountDialog'
import ReactivateLocationsDialog from 'components/customer-details/content/account-details/header/ReactivateLocationsDialog/ReactivateLocationsDialog'
import { useAccountSettlementRows } from 'components/customer-details/content/account-details/header/useAccountSettlementRows'
import EditAccountRepDialog from 'components/customer-details/content/account-rep/EditAccountRepDialog'
import DeactivateAccountDialog from 'components/customer-details/content/common/deactivate-account-dialog/DeactivateAccountDialog'
import ReactivateAccountDialog from 'components/customer-details/content/common/reactivate-account-dialog/ReactivateAccountDialog'
import SuspendAccountDialog from 'components/customer-details/content/suspensions/SuspendAccountDialog/SuspendAccountDialog'
import UnsuspendAccountDialog from 'components/customer-details/content/suspensions/UnsuspendAccountDialog/UnsuspendAccountDialog'
import { getWorkOrderListInvalidatesTags } from 'components/customer-details/content/work-orders/utils'
import VacationHoldDialog from 'components/customers/accounts/vacation-holds/VacationHoldDialog'
import AddToGroupDialog from 'components/customers/groups/account/AddToGroupDialog'
import SuspensionHistoryDialog from 'components/customers/groups/suspension/suspension-history/SuspensionHistoryDialog'
import DeactivateAccountDrawer from 'components/inventory/containers/configured-service/deactivation/DeactivateAccountDrawer'
import { pushDeactivationStartedToast } from 'components/notifications/notification-center/notificationCenterToast'
import { NOTIFICATION_PREF_KEY } from 'components/notifications/notification-center/notificationCenterViews'
import { useCanReceiveNotification } from 'components/notifications/notification-center/useIsNotificationCenterAvailable'
import { isWORedesignFeatureAvailable } from 'data/launch-darkly/featureSelectors'
import { selectIsInventoryPlacementsEnabled } from 'data/tenant/tenantSettingsSelectors'
import { getCustomerDetails } from 'middleware/actions/customers'
import { isDeactivationPending, isReactivationInProgress } from 'settings/constants/status'
import {
  selectIsActiveDeactivateAccountDialogOpen,
  selectIsChangeAccountRepresentativeDialogOpen,
  selectIsReactivateLocationsDialogOpen,
  setIsActiveDeactivateAccountDialogOpen,
  setIsChangeAccountRepresentativeDialogOpen,
  setIsReactivateLocationsDialogOpen,
} from 'slices/customer/customerDetailsSlice'
import T from 'T'
import { getEditAccountModel } from 'utils/customerPayload'
import { formatDateToBEFormat } from 'utils/date'
import { handleErrorWithFetchError } from 'utils/error'
import { getActiveDeactivatedLocations } from 'utils/location'

import type { DeactivateAccountResult } from 'components/inventory/containers/configured-service/deactivation/DeactivateAccountDrawer'
import type { DeactivationCaptureApi } from 'types/api/account/accountDeactivation'

const CAPTURE_FIELDS = ['lossReasonId', 'lossCompetitorId']

type AccountHeaderActionMenuProps = {
  anchorEl?: HTMLElement | null
  accountId: string
  onClose: () => void
}

type CustomerDetails = {
  accountName?: string
  accountNumber?: string
  active?: boolean
  isSuspended?: boolean
  deactivationStatus?: string | null
  reactivationInProgressSince?: string | null
}

type CustomersState = {
  CustomersReducer?: { customerDetails?: CustomerDetails | null }
}

type YardApi = {
  id?: string
  name?: string
}

type ContainerSettlement = {
  containerId: string
  outcome: string
  retrievalDate?: string
  yardId?: string
  leftOnSiteOutcome?: string
}

type DeactivationBody = DeactivationCaptureApi & { containerSettlements?: ContainerSettlement[] }

type PendingDeactivation = {
  resolve: (value?: unknown) => void
  reject: (reason?: unknown) => void
}

type DeactivationError = { data?: { exceptionType?: string } }

const AccountHeaderActionMenu = ({ anchorEl, accountId, onClose }: AccountHeaderActionMenuProps) => {
  const dispatch = useDispatch()
  const [isOpenEditAccountDialog, setIsOpenEditAccountDialog] = useState(false)
  const [isOpenAddToGroupDialog, setIsOpenAddToGroupDialog] = useState(false)
  const [isOpenSuspendDialog, setIsOpenSuspendDialog] = useState(false)
  const [isOpenUnsuspendDialog, setIsOpenUnsuspendDialog] = useState(false)
  const [isOpenSuspensionHistoryDialog, setIsOpenSuspensionHistoryDialog] = useState(false)
  const [isOpenVacationHoldDialog, setIsOpenVacationHoldDialog] = useState(false)
  const [isOpenReactivateDialog, setIsOpenReactivateDialog] = useState(false)
  // A capture waiting on the settlement drawer, and the dialog's promise to settle once that answers.
  const [pendingCapture, setPendingCapture] = useState<DeactivationCaptureApi | null>(null)
  const pendingDeactivation = useRef<PendingDeactivation | null>(null)
  const flags = useFlags()
  const isWORedesignEnabled = isWORedesignFeatureAvailable({ flags })

  const handleEditAccount = useCallback(() => {
    onClose()
    setIsOpenEditAccountDialog(true)
  }, [onClose])

  const onCloseEditAccountDialog = useCallback(() => {
    setIsOpenEditAccountDialog(false)
  }, [])

  const [requestDeactivation, { isLoading: isRequestDeactivationLoading }] = useRequestAccountDeactivationWithCaptureMutation()
  const [deactivateNow, { isLoading: isDeactivateNowLoading }] = useDeactivateAccountWithCaptureMutation()
  const isDeactivating = isRequestDeactivationLoading || isDeactivateNowLoading
  const canReceiveDeactivationNotification = useCanReceiveNotification(NOTIFICATION_PREF_KEY.ACCOUNT_DEACTIVATION)
  const isOpenReactivateLocationsDialog = useSelector(selectIsReactivateLocationsDialogOpen)
  const isOpenChangeAccountRepresentativeDialog = useSelector(selectIsChangeAccountRepresentativeDialogOpen)
  const isOpenActiveDeactivateAccountDialog = useSelector(selectIsActiveDeactivateAccountDialogOpen)
  const isInventoryPlacementsEnabled = useSelector(selectIsInventoryPlacementsEnabled, shallowEqual)
  const customerDetails = useSelector((state: CustomersState) => state.CustomersReducer?.customerDetails ?? null)
  const isActiveAccount = customerDetails?.active
  const isAccountSuspended = customerDetails?.isSuspended
  const isDeactivationInFlight = isDeactivationPending(customerDetails?.deactivationStatus)
  const isRegeneratingWorkOrders = isReactivationInProgress(customerDetails?.reactivationInProgressSince)
  const deactivateBlockedReason =
    (isDeactivationInFlight && T.ACCOUNT_DEACTIVATION_ALREADY_RUNNING) ||
    (isActiveAccount && isRegeneratingWorkOrders && T.ACCOUNT_WORK_ORDERS_STILL_REGENERATING)
  const { activeLocations } = useMemo(() => getActiveDeactivatedLocations(customerDetails, true), [customerDetails])
  const accountEditModel = useMemo(() => getEditAccountModel(customerDetails), [customerDetails])

  const handleReactivateLocationsAction = useCallback(() => {
    onClose()
    dispatch(setIsReactivateLocationsDialogOpen(true))
  }, [onClose, dispatch])

  const handleCloseReactivateLocationsDialog = useCallback(() => {
    dispatch(setIsReactivateLocationsDialogOpen(false))
  }, [dispatch])

  const handleChangeAccountRepAction = useCallback(() => {
    onClose()
    dispatch(setIsChangeAccountRepresentativeDialogOpen(true))
  }, [onClose, dispatch])

  const handleCloseChangeAccountRepresentativeDialog = useCallback(() => {
    dispatch(setIsChangeAccountRepresentativeDialogOpen(false))
  }, [dispatch])

  const handleActivateDeactivateSuccess = useCallback(() => {
    onClose()
    dispatch(getCustomerDetails({ accountId }))
    const woInvalidateTags = getWorkOrderListInvalidatesTags({
      isNew: isWORedesignEnabled,
      serviceDate: undefined,
      woSectionOnly: undefined,
      sectionWise: true,
      csId: undefined,
    })
    dispatch(api.util.invalidateTags([CACHE_TAG_CONFIGURED_SERVICES_LIST, ...woInvalidateTags]))
  }, [onClose, dispatch, accountId, isWORedesignEnabled])

  const locations = useCallback((): string[] => activeLocations.map((location: { id: string }) => location.id), [activeLocations])

  // Read as soon as the menu opens, so Deactivate opens the right surface without a flicker.
  const {
    rows: settlementRows,
    isFetching: isFetchingSettlementRows,
    hasError: hasSettlementRowError,
  } = useAccountSettlementRows(accountId, isInventoryPlacementsEnabled && Boolean(anchorEl))
  const { data: yardListData } = useGetYardListQuery(undefined)
  const yards = useMemo(
    () => (yardListData?.yards ?? []).map((yard: YardApi | null) => ({ id: yard?.id ?? '', name: yard?.name ?? '' })),
    [yardListData]
  )
  // An account with nothing on it has no question to ask, so the dialog submits on its own.
  const hasContainersToSettle =
    isInventoryPlacementsEnabled && (settlementRows.length > 0 || isFetchingSettlementRows || hasSettlementRowError)

  // The backend ignores it until the settlement write lands; sending it is what says what we need.
  const containerSettlementBody = useCallback((result?: DeactivateAccountResult): DeactivationBody => {
    if (!result || result.resolveLater) return {}
    const containerSettlements = Object.entries(result.resolutions ?? {}).map(([containerId, resolution]) => ({
      containerId,
      outcome: resolution.kind,
      ...(resolution.date && { retrievalDate: formatDateToBEFormat(resolution.date) }),
      ...(resolution.yardId && { yardId: resolution.yardId }),
      ...(resolution.leftOutcome && { leftOnSiteOutcome: resolution.leftOutcome }),
    }))

    return containerSettlements.length > 0 ? { containerSettlements } : {}
  }, [])

  // The async route reports through the notification center, so a user who cannot receive it keeps the synchronous one.
  const submitDeactivation = useCallback(
    (body: DeactivationBody) => {
      if (!canReceiveDeactivationNotification) {
        return deactivateNow({ accountId, locations: locations(), ...body })
          .unwrap()
          .then(() => {
            dispatch(setIsActiveDeactivateAccountDialogOpen(false))
            handleActivateDeactivateSuccess()
            toast.success(T.ACCOUNT_DEACTIVATED_SUCCESSFULLY)
          })
      }

      // Scheduled, not done: refetching the account is enough, and it brings back the pending status.
      return requestDeactivation({ accountId, locations: locations(), ...body })
        .unwrap()
        .then(() => {
          dispatch(setIsActiveDeactivateAccountDialogOpen(false))
          dispatch(getCustomerDetails({ accountId }))
          pushDeactivationStartedToast(accountId)
        })
    },
    [
      canReceiveDeactivationNotification,
      deactivateNow,
      requestDeactivation,
      accountId,
      locations,
      dispatch,
      handleActivateDeactivateSuccess,
    ]
  )

  // The dialog hides rather than closes, so a capture field the drawer's request gets refused on comes back marked.
  const handleAccountDeactivation = useCallback(
    (capture: DeactivationCaptureApi): Promise<unknown> => {
      if (!hasContainersToSettle) return submitDeactivation(capture)
      setPendingCapture(capture)
      return new Promise((resolve, reject) => {
        pendingDeactivation.current = { resolve, reject }
      })
    },
    [hasContainersToSettle, submitDeactivation]
  )

  const settlePendingDeactivation = useCallback((settle: (pending: PendingDeactivation) => void) => {
    const pending = pendingDeactivation.current
    pendingDeactivation.current = null
    if (pending) settle(pending)
    setPendingCapture(null)
  }, [])

  // Any failure the dialog cannot mark keeps the drawer and its answers, so a retry does not mean settling again.
  const handleSettlementConfirm = useCallback(
    (result: DeactivateAccountResult) => {
      submitDeactivation({ ...pendingCapture, ...containerSettlementBody(result) })
        .then(() => settlePendingDeactivation(pending => pending.resolve()))
        .catch((error: DeactivationError) => {
          if (!CAPTURE_FIELDS.includes(error?.data?.exceptionType ?? '')) {
            handleErrorWithFetchError(error, T.ACCOUNT_DEACTIVATION_FAILED)
            return
          }
          settlePendingDeactivation(pending => pending.reject(error))
        })
    },
    [pendingCapture, containerSettlementBody, submitDeactivation, settlePendingDeactivation]
  )

  const handleSettlementClose = useCallback(() => settlePendingDeactivation(pending => pending.resolve()), [settlePendingDeactivation])

  const handleAccountActivateDeactivateAction = useCallback(() => {
    onClose()
    if (isActiveAccount) {
      dispatch(setIsActiveDeactivateAccountDialogOpen(true))
      return
    }
    setIsOpenReactivateDialog(true)
  }, [onClose, isActiveAccount, dispatch])

  const handleCloseReactivateDialog = useCallback(() => setIsOpenReactivateDialog(false), [])

  const handleCloseAccountActivateDeactivateDialog = useCallback(() => {
    dispatch(setIsActiveDeactivateAccountDialogOpen(false))
  }, [dispatch])

  const handleSuspendAction = useCallback(() => {
    if (isAccountSuspended) {
      onClose()
      setIsOpenUnsuspendDialog(true)
    } else {
      onClose()
      setIsOpenSuspendDialog(true)
    }
  }, [isAccountSuspended, onClose])

  const handleCloseSuspendDialog = useCallback(() => {
    setIsOpenSuspendDialog(false)
  }, [])

  const handleCloseUnsuspendDialog = useCallback(() => {
    setIsOpenUnsuspendDialog(false)
  }, [])

  const handleOpenAddToGroupDialog = useCallback(() => {
    onClose()
    setIsOpenAddToGroupDialog(true)
  }, [onClose])

  const handleCloseAddToGroupDialog = useCallback(() => setIsOpenAddToGroupDialog(false), [])

  const handleOpenSuspensionHistoryDialog = useCallback(() => {
    onClose()
    setIsOpenSuspensionHistoryDialog(true)
  }, [onClose])

  const handleCloseSuspensionHistoryDialog = useCallback(() => setIsOpenSuspensionHistoryDialog(false), [])

  const handleOpenVacationHoldDialog = useCallback(() => {
    onClose()
    setIsOpenVacationHoldDialog(true)
  }, [onClose])

  const handleCloseVacationHoldDialog = useCallback(() => {
    setIsOpenVacationHoldDialog(false)
  }, [])

  return (
    <>
      <Menu
        disableScrollLock
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={Boolean(anchorEl)}
        PaperProps={{ variant: 'outlined' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={onClose}
      >
        <MenuItem onClick={handleEditAccount}>{T.EDIT}</MenuItem>

        <Tooltip arrow title={!isActiveAccount && T.REACTIVATE_ACCOUNT}>
          <span>
            <MenuItem disabled={!isActiveAccount} onClick={handleReactivateLocationsAction}>
              {T.REACTIVATE_MULTIPLE_LOCATION}
            </MenuItem>
          </span>
        </Tooltip>
        <MenuItem onClick={handleOpenAddToGroupDialog}>{T.ADD_TO_ACCOUNT_GROUP}</MenuItem>
        <MenuItem onClick={handleChangeAccountRepAction}>{T.CHANGE_ACCOUNT_REP}</MenuItem>
        <MenuItem onClick={handleOpenSuspensionHistoryDialog}>{T.SUSPENSION_HISTORY}</MenuItem>
        <MenuItem onClick={handleOpenVacationHoldDialog}>{T.HOLDS}</MenuItem>
        <Tooltip arrow title={deactivateBlockedReason}>
          <span>
            <MenuItem
              disabled={Boolean(deactivateBlockedReason)}
              sx={{ color: isActiveAccount ? 'warning.main' : 'success.main' }}
              onClick={handleAccountActivateDeactivateAction}
            >
              {isActiveAccount ? T.DEACTIVATE : T.REACTIVATE}
            </MenuItem>
          </span>
        </Tooltip>
        <MenuItem sx={{ color: isAccountSuspended ? 'success.main' : 'error.main' }} onClick={handleSuspendAction}>
          {isAccountSuspended ? T.UNSUSPEND_ACCOUNT : T.SUSPEND_ACCOUNT}
        </MenuItem>
      </Menu>
      <ReactivateLocationsDialog
        accountId={accountId}
        open={isOpenReactivateLocationsDialog}
        onClose={handleCloseReactivateLocationsDialog}
      />
      <EditAccountRepDialog
        customerDetails={customerDetails ?? undefined}
        isOpen={isOpenChangeAccountRepresentativeDialog}
        onClose={handleCloseChangeAccountRepresentativeDialog}
      />
      <DeactivateAccountDialog
        accountName={customerDetails?.accountName ?? ''}
        accountNumber={customerDetails?.accountNumber}
        isHidden={pendingCapture !== null}
        isLoading={isDeactivating}
        isOpen={isOpenActiveDeactivateAccountDialog}
        runsInBackground={canReceiveDeactivationNotification}
        onClose={handleCloseAccountActivateDeactivateDialog}
        onConfirm={handleAccountDeactivation}
      />
      {(hasContainersToSettle || pendingCapture !== null) && (
        <DeactivateAccountDrawer
          accountName={customerDetails?.accountName ?? ''}
          defaultYardId={yards[0]?.id}
          description={T.DEACTIVATE_ACCOUNT_SETTLE_CONTAINERS}
          hasRowError={hasSettlementRowError}
          isFetchingRows={isFetchingSettlementRows}
          isLoading={isDeactivating}
          isOpen={pendingCapture !== null}
          rows={settlementRows}
          yards={yards}
          onClose={handleSettlementClose}
          onConfirm={handleSettlementConfirm}
        />
      )}
      <SuspendAccountDialog accountId={accountId} open={isOpenSuspendDialog} onClose={handleCloseSuspendDialog} />
      <UnsuspendAccountDialog accountId={accountId} open={isOpenUnsuspendDialog} onClose={handleCloseUnsuspendDialog} />
      <EditAccountDialog
        account={accountEditModel}
        customerDetails={customerDetails ?? undefined}
        isOpen={isOpenEditAccountDialog}
        onClose={onCloseEditAccountDialog}
      />
      <AddToGroupDialog accountIds={[accountId]} isOpen={isOpenAddToGroupDialog} onClose={handleCloseAddToGroupDialog} />
      <SuspensionHistoryDialog accountId={accountId} open={isOpenSuspensionHistoryDialog} onClose={handleCloseSuspensionHistoryDialog} />
      <VacationHoldDialog open={isOpenVacationHoldDialog} row={{ accountId }} onClose={handleCloseVacationHoldDialog} />
      <ReactivateAccountDialog
        accountId={accountId}
        accountName={customerDetails?.accountName ?? ''}
        accountNumber={customerDetails?.accountNumber}
        isOpen={isOpenReactivateDialog}
        onClose={handleCloseReactivateDialog}
        onReactivated={handleActivateDeactivateSuccess}
      />
    </>
  )
}

export default AccountHeaderActionMenu
